import * as React from 'react'
import moment from 'moment'
import AddUserColor from '../../../../assets/icons_new/AddUserColor.svg'
import RefreshColor from '../../../../assets/icons_new/RefreshColor.svg'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'

import { SelectionGroup } from '@/modules/drivers/context/TableContext'
import { DriverFormProvider } from '../../context/DriverFormContext'
import AddDriverModal from '../../modals/driverAdd/AddDriverModal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { ReportType } from '@/app/constants/reportType'
import { TaskType } from '@/app/constants/taskType'
import { SERVICE_TIME_FORMAT } from '@/app/constants/date'
import usePrintService from '@/modules/drivers/services/PrintService'
import { useTask } from '@/modules/shared/context/TaskContext'
import { PrintActionButton } from '@/modules/printouts/components/PrintActionButton'
import { usePrintJobGuard } from '@/modules/printouts/hooks/usePrintJobGuard'
import { toast } from '@/lib/toasts/api'
import DriverActions from './details/DriverActions'
import ActionsContainer from './details/ActionsContainer'
import ModalsContainer from './details/ModalsContainer'
import { useSideSelectionStore } from '../../stores/sideSelectionStore'
import { useHasAccess } from '@/modules/shared/permissions'

const ContractActionBar = ({
  companyId,
  driverIds,
}: {
  companyId: string
  driverIds: number[]
}) => {
  const { sideCheckedIds, sideIsActive, clearSide } = useSideSelectionStore()
  const { basicDrivers, refresh } = useDrivers()
  const { t } = useLocalize()
  const [isOpenAddDriverModal, setIsOpenAddDriverModal] = React.useState(false)
  const { allowed: canEditDrivers } = useHasAccess({ permissions: 'canAddDriver' }, companyId)
  const [isPrinting, setIsPrinting] = React.useState(false)
  const { getPdf } = usePrintService()
  const { addTask } = useTask()
  const { busyTooltip, runPrintJob } = usePrintJobGuard()

  const selectedGroup: SelectionGroup = React.useMemo(
    () =>
      sideCheckedIds.length > 0
        ? { ids: sideCheckedIds, isActive: sideIsActive }
        : null,
    [sideCheckedIds, sideIsActive],
  )

  const selectedDriverNames = React.useMemo(
    () =>
      sideCheckedIds
        .map((id) => {
          const d = basicDrivers.find((dr) => dr.id === id)
          return d ? d.fullName ?? '' : ''
        })
        .filter(Boolean),
    [sideCheckedIds, basicDrivers],
  )

  const printDriversList = React.useCallback(async () => {
    if (isPrinting) return

    if (driverIds.length === 0) {
      toast.warning({ text: t('driverModule.select-drivers') })
      return
    }

    try {
      setIsPrinting(true)
      const startTime = moment()
        .subtract(1, 'year')
        .startOf('day')
        .format(SERVICE_TIME_FORMAT)
      const endTime = moment().endOf('day').format(SERVICE_TIME_FORMAT)

      const taskId = await getPdf({
        companyId,
        reportType: ReportType.DRIVERS_LIST,
        drivers: driverIds,
        startTime,
        endTime,
        data: { separateFiles: false },
      })

      addTask(taskId, TaskType.Printout, ReportType.DRIVERS_LIST)
    } catch (error) {
      console.error('Error printing drivers list:', error)
      toast.error({ text: t('printout.generation-error') })
    } finally {
      setIsPrinting(false)
    }
  }, [driverIds, companyId, getPdf, addTask, t, isPrinting])

  const handlePrintTable = React.useCallback(
    () => runPrintJob(printDriversList),
    [printDriversList, runPrintJob],
  )

  return (
    <div className="tw-relative tw-z-[100] tw-w-full">
      <div className="tw-flex tw-w-full tw-justify-between tw-px-4 tw-py-1.5 md:tw-items-center">
        <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row md:tw-items-center">
          <p className="tw-text-sm">
            <span className="tw-font-medium">{sideCheckedIds.length}</span>{' '}
            {t('driverModule.drivers-count')}
          </p>
          <DividerVertical />
          {canEditDrivers && (
            <>
              <div
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-p-2 hover:tw-bg-base-300 hover:tw-opacity-90"
                onClick={() => setIsOpenAddDriverModal(true)}
              >
                <img src={AddUserColor} alt="Add Driver Icon" />
                <p className="tw-text-sm tw-font-medium tw-text-primary">
                  {t('driverModule.add-driver')}
                </p>
              </div>
              <DividerVertical />
            </>
          )}
          <DriverActions
            companyId={companyId}
            selectedGroup={selectedGroup}
            onReset={clearSide}
          />
          <DividerVertical />
          <ActionsContainer
            selectedGroup={selectedGroup}
            companyId={companyId}
          />
        </div>

        <div className="tw-flex tw-items-center tw-gap-3">
          <PrintActionButton
            tooltip={'Drukuj listę kierowców'}
            onClick={handlePrintTable}
            blockedText={busyTooltip}
          />
          <div
            className="tw-cursor-pointer hover:tw-opacity-90"
            onClick={() => refresh()}
          >
            <img src={RefreshColor} alt="Refresh Icon" />
          </div>
        </div>
      </div>

      {isOpenAddDriverModal && (
        <DriverFormProvider companyId={companyId}>
          <AddDriverModal
            companyId={companyId}
            isOpen={isOpenAddDriverModal}
            close={() => setIsOpenAddDriverModal(false)}
          />
        </DriverFormProvider>
      )}

      <ModalsContainer
        companyId={companyId}
        selectedDriverNames={selectedDriverNames}
        selectedGroupOverride={selectedGroup}
        onResetOverride={clearSide}
      />
    </div>
  )
}

export default ContractActionBar

const DividerVertical = () => (
  <div className="tw-hidden tw-h-5 tw-w-0.5 tw-bg-secondary lg:tw-block"></div>
)
